import type { SlateLeaf, SlateLeafConstant } from '@atlas/renderer';
import isHtmlElementNode from '@atlas/tool/box/dom/isHtmlElementNode';
import isNodeBreakingElement from '@atlas/tool/box/slate/isNodeBreakingElement';
import isTextNode from '@atlas/tool/box/dom/isTextNode';
import isCommentNode from '@atlas/tool/box/dom/isCommentNode';
import getConstantValue from '@atlas/tool/box/slate/getConstantValue';

export default function extractNodeListText(nodeList: NodeListOf<ChildNode>, keepSpaces = false): SlateLeaf[] {
    const list = Array.from(nodeList);
    const leaves: SlateLeaf[] = [];

    for (let i = 0; i < list.length; i++) {
        const node = list[i];
        const nodeIsElement = isHtmlElementNode(node);

        if (!nodeIsElement) {
            if (isTextNode(node) && node.textContent?.length) {
                leaves.push({ text: node.textContent });
            }
            node.remove();
            continue;
        }

        if (
            isCommentNode(node) ||
            isHtmlElementNode(node, 'STYLE') ||
            isHtmlElementNode(node, 'SCRIPT') ||
            isNodeBreakingElement(node)
        )
            break;

        const marks: Omit<SlateLeaf, 'text'> = {};

        if (isHtmlElementNode(node, 'A') && node.href) marks.link = node.href;

        if (
            node.dataset.constant &&
            node.dataset.constant.startsWith(node.innerText + ':') &&
            getConstantValue(node.dataset.constant as SlateLeafConstant)
        ) {
            marks.constant = node.dataset.constant as SlateLeafConstant;
        }

        if (marksTagMap[node.tagName]) marks[marksTagMap[node.tagName]] = true;

        if (
            !marks.bold &&
            (['bold', 'bolder'].includes(node.style.fontWeight) || parseInt(node.style.fontWeight) >= 500)
        ) {
            marks.bold = true;
        }

        if (!marks.italic && node.style.fontStyle === 'italic') marks.italic = true;

        if (!marks.underline && node.style.textDecoration === 'underline') {
            marks.underline = true;
        }

        if (!marks.strikethrough && node.style.textDecoration === 'line-through') {
            marks.strikethrough = true;
        }

        const leavesFromNode = extractNodeListText(node.childNodes, keepSpaces);
        leaves.push(
            ...(Object.keys(marks).length ? leavesFromNode.map((leaf) => Object.assign(leaf, marks)) : leavesFromNode),
        );

        if (node.hasChildNodes()) break;

        node.remove();
    }

    if (keepSpaces) return leaves;

    return leaves
        .map((leaf, index) => {
            const noBreaksLeaf = leaf.text.includes('\n') ? { ...leaf, text: leaf.text.replace(/[\s]+/, ' ') } : leaf;

            if (!noBreaksLeaf.text) return null;

            if (index > 0 && noBreaksLeaf.text[0] === ' ') {
                // Avoid several spaces in a row between different leaves
                const prevLeaf = leaves[index - 1];
                if (prevLeaf.text[prevLeaf.text.length - 1] === ' ') {
                    return { ...noBreaksLeaf, text: noBreaksLeaf.text.slice(1) };
                }
            }

            return noBreaksLeaf;
        })
        .filter((v: SlateLeaf | null): v is SlateLeaf => v !== null);
}

// #region Helpers

const marksTagMap: Record<string, 'bold' | 'italic' | 'underline' | 'strikethrough' | 'code'> = {
    STRONG: 'bold',
    B: 'bold',
    I: 'italic',
    EM: 'italic',
    U: 'underline',
    S: 'strikethrough',
    STRIKE: 'strikethrough',
    CODE: 'code',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('extractNodeListText', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
